import React from 'react'
import Reveal3 from '@/components/marketing3/Reveal3'
import GuardrailBand from '@/components/marketing3/widgets/GuardrailBand'
import OverridePin from '@/components/marketing3/widgets/OverridePin'

/**
 * Control3 (light) — "Automation you can trust — because you set the terms." Four cards
 * (Guardrails you set / You always have the last word / Your rules, per property /
 * Clean, guest-ready prices) plus the two Control widgets: GuardrailBand (W6) and
 * OverridePin (W7).
 *
 * Copy is quoted VERBATIM from docs/marketing/2026-07-05-landing3-copy.md §Control.
 * Per plan §0/§2.4 the v1 "assistant-mode" card is retired here; the reassigned agent
 * vocabulary now lives in the AI section and must not appear in this one. No PMS name
 * (say "your PMS"). Illustrative money figures live in the widgets only. Teal is the
 * sole accent.
 *
 * Cards 3 and 4 were swapped in 2026-07-05 (founder direction): the two retired cards
 * leaked compute cadence and rested on an unguaranteed promise. Both replacements are
 * LIVE-backed (per-property tenant model; whole-pound / clean pricing — see
 * claims-register §1 and the copy doc's §Control).
 */

const CARDS: { lead: string; body: React.ReactNode }[] = [
  {
    lead: 'Guardrails you set.',
    body: (
      <>
        Price Guardrails are your safety net — a floor and a ceiling no rate will ever cross, whatever the demand
        signal says.
      </>
    ),
  },
  {
    lead: 'You always have the last word.',
    body: (
      <>
        Know something the engine doesn&rsquo;t — a coach party, a quiet week? Override it. An override beats the
        engine, every time, and stays until you say otherwise.
      </>
    ),
  },
  {
    lead: 'Your rules, per property.',
    body: (
      <>
        Every property runs its own guardrails, its own switch, its own rules. Nothing is one-size-fits-all — each
        hotel is priced on its own terms.
      </>
    ),
  },
  {
    lead: 'Clean, guest-ready prices.',
    body: (
      <>
        Prices always land clean, to your own rounding rules — never a £103.47 on your booking page.
      </>
    ),
  },
]

export default function Control3(_props: { brand: string }) {
  return (
    <section id="control" className="scroll-mt-24" aria-labelledby="control-heading">
      <div className="mx-auto max-w-6xl px-4 py-24 sm:px-6 sm:py-28">
        <Reveal3 className="max-w-3xl">
          <p className="mkt3-eyebrow">Control</p>
          <h2 id="control-heading" className="mkt3-display mkt3-h2 mt-5">
            Automation you can trust — because you set the terms.
          </h2>
        </Reveal3>

        <div className="mt-14 grid gap-4 sm:grid-cols-2">
          {CARDS.map((card, i) => (
            <Reveal3 key={card.lead} delay={i * 70}>
              <div className="mkt3-panel mkt3-lift h-full p-6">
                <h3 className="text-[15px] font-semibold text-slate-900">{card.lead}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{card.body}</p>
              </div>
            </Reveal3>
          ))}
        </div>

        {/* the two Control widgets — a clamped guardrail band and a pinned override */}
        <div className="mt-6 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <Reveal3 delay={80}>
            <GuardrailBand className="h-full" />
          </Reveal3>
          <Reveal3 delay={160}>
            <OverridePin className="h-full" />
          </Reveal3>
        </div>
      </div>
    </section>
  )
}
